import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./cn";

const badgeVariants = cva(
  [
    "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full",
    "text-[11px] font-bold uppercase tracking-widest whitespace-nowrap",
  ].join(" "),
  {
    variants: {
      tone: {
        ink: "bg-ink text-paper",
        lime: "bg-lime text-ink",
        ember: "bg-ember text-paper",
        sun: "bg-sun text-ink",
        smoke: "bg-smoke text-ash",
        mist: "bg-mist text-ink",
        paper: "bg-paper text-ink",
        "lime-soft": "bg-lime/15 text-lime-dark",
        "ember-soft": "bg-ember/15 text-ember-dark",
        "sun-soft": "bg-sun/30 text-ink",
        "danger-soft": "bg-danger/10 text-danger",
        "info-soft": "bg-info/10 text-info",
        "white-soft": "bg-white/20 text-white",
      },
    },
    defaultVariants: {
      tone: "mist",
    },
  },
);

export type BadgeProps = React.HTMLAttributes<HTMLSpanElement> &
  VariantProps<typeof badgeVariants> & {
    withDot?: boolean;
    pulse?: boolean;
  };

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, tone, withDot, pulse, children, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(badgeVariants({ tone }), className)}
      {...props}
    >
      {withDot ? (
        <span
          className={cn(
            "h-1.5 w-1.5 rounded-full bg-current",
            pulse && "animate-pulse-dot",
          )}
        />
      ) : null}
      {children}
    </span>
  ),
);
Badge.displayName = "Badge";

export { badgeVariants };
